/**
 * @file     ListingSectionNav.tsx
 * @purpose  The slug page's in-body section anchors (SectionAnchors under a
 *           hairline), only for sections that rendered, plus the listener
 *           that turns openDetailTab(key) calls (monthly estimate, See all
 *           costs) into a scroll to the section with that id. Each jump keeps the property_detail_tab_changed
 *           event the tabs used to send, so the funnel reads the same.
 */
'use client';

import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import Box from '@mui/material/Box';
import { SectionAnchors, type SectionAnchorItem } from '@/components/hub/SectionAnchors';
import { useAnalytics } from '@/hooks/useAnalytics';
import { analyticsEvents } from '@/core/constants/analytics';
import { DETAIL_TAB_OPEN_EVENT, type DetailTabTrigger } from './DetailTabsShell';

interface Props {
  items: SectionAnchorItem[];
  ariaLabel: string;
  listingKey: string;
}

/** Sections that stream in after the body (Suspense) and may never render. */
const STREAMED_IDS = new Set(['similar']);

const sameIds = (a: SectionAnchorItem[], b: SectionAnchorItem[]) =>
  a.length === b.length && a.every((item, i) => item.id === b[i]?.id);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

export function ListingSectionNav({ items, ariaLabel, listingKey }: Props) {
  const { trackEvent } = useAnalytics();
  // Anchors only for sections that rendered. The server HTML leaves out the
  // streamed similar-homes link; once mounted, the list is whatever ids the
  // page actually holds, and it picks up #similar when that block lands.
  const [rendered, setRendered] = useState(() => items.filter((item) => !STREAMED_IDS.has(item.id)));
  useEffect(() => {
    const update = () => {
      const next = items.filter((item) => document.getElementById(item.id) != null);
      setRendered((prev) => (sameIds(prev, next) ? prev : next));
    };
    update();
    let frame = 0;
    const observer = new MutationObserver(() => {
      if (!frame) frame = window.requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    });
    observer.observe(document.querySelector('article') ?? document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [items]);
  const currentRef = useRef(items[0]?.id ?? '');

  const track = useCallback(
    (to: string, trigger: DetailTabTrigger) => {
      const from = currentRef.current;
      currentRef.current = to;
      trackEvent({
        eventName: analyticsEvents.propertyDetailTabChanged,
        eventType: 'click',
        category: 'property_detail',
        label: to,
        metadata: { listing_key: listingKey, surface: 'slug_page', from_tab: from, to_tab: to, trigger },
      });
    },
    [listingKey, trackEvent],
  );

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ key: string; trigger?: DetailTabTrigger }>).detail;
      const target = detail?.key ? document.getElementById(detail.key) : null;
      if (!target) return;
      track(detail.key, detail.trigger ?? 'programmatic');
      target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    };
    window.addEventListener(DETAIL_TAB_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(DETAIL_TAB_OPEN_EVENT, onOpen);
  }, [track]);

  if (rendered.length < 2) return null;

  return (
    <Box
      data-listing-section-nav
      onClickCapture={(event: MouseEvent<HTMLElement>) => {
        const link = (event.target as HTMLElement).closest('a[href^="#"]');
        const id = link?.getAttribute('href')?.slice(1);
        if (id && id !== currentRef.current) track(id, 'tab_click');
      }}
      sx={{ mb: { xs: 6, md: 8 } }}
    >
      <SectionAnchors items={rendered} ariaLabel={ariaLabel} firstCurrentAtTop={false} />
    </Box>
  );
}
